'use client';
import {Box} from '@mui/material';
import {styled, type SxProps, type Theme} from '@mui/material/styles';
import {useMemo, type MouseEvent} from 'react';
import {useTranslation} from 'react-i18next';
import {useClampOverflow} from '../../../hooks';
import {toRichHtml} from '../../../shared/utils/rich-html';
import {Button, DisclosureChevron, type FontFamily, type FontSize, type FontWeight, type LineHeight} from '../../primitives';

export interface RichContentProps {
  html: string;
  collapsible?: boolean;
  collapsedLines?: number;
  size?: FontSize;
  weight?: FontWeight;
  lineHeight?: LineHeight;
  family?: FontFamily;
  className?: string;
  sx?: SxProps<Theme>;
  onLinkClick?: (href: string) => void;
}

const Root = styled(Box)(({theme}) => ({
  position: 'relative',
  color: theme.palette.colors.text.neutral.default,
}));

const Fade = styled(Box)(({theme}) => ({
  position: 'absolute',
  insetInline: 0,
  bottom: 0,
  height: theme.spacing(6),
  pointerEvents: 'none',
}));

const Prose = styled(Box, {
  shouldForwardProp: propName => !['$clamp', '$lines', '$size', '$weight', '$lineHeight', '$family'].includes(propName as string),
})<{
  $clamp: boolean;
  $lines: number;
  $size: FontSize;
  $weight: FontWeight;
  $lineHeight?: LineHeight;
  $family: FontFamily;
}>(({theme, $clamp, $lines, $size, $weight, $lineHeight, $family}) => {
  return {
    color: 'inherit',
    fontFamily: theme.font.family[$family],
    fontSize: theme.font.size[$size],
    fontWeight: theme.font.weight[$weight],
    lineHeight: $lineHeight ? theme.font.lineHeight[$lineHeight] : 1.2,
    '& p': {margin: 0},
    '& p + p': {marginTop: theme.spacing(1)},
    '& p + ul, & p + ol, & ul + p, & ol + p': {marginTop: theme.spacing(2)},
    '& strong, & b': {fontWeight: theme.typography.fontWeightBold},
    '& em, & i': {fontStyle: 'italic'},
    '& ul, & ol': {
      margin: 0,
      paddingInlineStart: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.75),
    },
    '& a': {
      color: theme.palette.colors.text.primary.default,
      textDecoration: 'underline',
      textUnderlineOffset: 2,
    },
    ...($clamp && {
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: $lines,
      overflow: 'hidden',
    }),
  };
});

export function RichContent({
  html,
  collapsible = true,
  collapsedLines = 3,
  size = 'body-2',
  weight = 'regular',
  lineHeight = '24',
  family = 'secondary',
  className,
  sx,
  onLinkClick,
}: RichContentProps) {
  const {t} = useTranslation();
  const normalized = useMemo(() => toRichHtml(html), [html]);
  const {proseRef, safe, clamp, overflowing, expanded, toggle} = useClampOverflow(normalized, collapsible);
  const moreLabel = t('read_more');
  const lessLabel = t('read_less');

  const showToggle = collapsible && (overflowing || expanded);

  // Delegate clicks on the sanitized body so a link anywhere inside it reports its href.
  const handleClick = onLinkClick
    ? (event: MouseEvent<HTMLElement>) => {
        const anchor = (event.target as HTMLElement).closest('a');
        const href = anchor?.getAttribute('href');
        if (href) {
          onLinkClick(href);
        }
      }
    : undefined;

  return (
    <Root className={className} sx={sx}>
      <Prose
        ref={proseRef}
        $clamp={clamp}
        $lines={collapsedLines}
        $size={size}
        $weight={weight}
        $lineHeight={lineHeight}
        $family={family}
        onClick={handleClick}
        dangerouslySetInnerHTML={{__html: safe}}
      />
      {clamp && overflowing && <Fade />}
      {showToggle && (
        <Button variant="link" onClick={toggle} iconRight={<DisclosureChevron open={expanded} />}>
          {expanded ? lessLabel : moreLabel}
        </Button>
      )}
    </Root>
  );
}

RichContent.displayName = 'RichContent';
